import styles from "./styles.module.scss";
import { useContext, useEffect, useState } from "react";
import { useLocation } from "react-router-dom";
import dayjs from "dayjs";
import customParseFormat from "dayjs/plugin/customParseFormat";

import { TranslationContext } from "../../../../../../contexts/translation/index.context";
import { FormContext } from "../../../../../../contexts/form/index.context";
import { ModalContext } from "../../../../../../contexts/modal/index.context";
import { TableContext } from "../../../../../../contexts/table/index.context";
import { SessionContext } from "../../../../../../contexts/session/index.context";
import { generatePassword } from "../../../../../../services/generate-password";
import { MergedColumnType } from "../../../../../ui/table/utils/types";
import { request } from "../../../../../../services";
//Components
import Drawer from "../../../../../ui/drawer";
import Directories from "../../../../../directories";
//UI
import Form from "antd/lib/form";
import Input from "antd/lib/input";
import Typography from "antd/lib/typography";
import Space from "antd/lib/space";
import Select from "antd/lib/select";
import Checkbox from "antd/lib/checkbox";
import Tooltip from "antd/lib/tooltip";
import Button from "antd/lib/button";
import InputNumber from "antd/lib/input-number";
import message from "antd/lib/message";
import DatePicker from "antd/lib/date-picker";
//Icons
import KeyOutlined from "@ant-design/icons/KeyOutlined";
//Locale
import "dayjs/locale/uk";
import { DrawerProvider } from "../../../../../../contexts/drawer/index.context";

dayjs.extend(customParseFormat);
dayjs.locale("uk");

const { Title } = Typography;

export function ConfiguringPageAccess({ pathname }: { pathname: string }) {
  const location = useLocation();
  const { intl } = useContext(TranslationContext);
  const { form } = useContext(FormContext);
  const { activeModal } = useContext(ModalContext);
  const edit = activeModal === "edit";
  const [rolesData, setRolesData] = useState<MergedColumnType[]>([]);
  const [pagesData, setPagesData] = useState<MergedColumnType[]>([]);

  const rolesOptions = rolesData.map((item) => ({
    value: item.id,
    label: item.name,
  }));

  const pagesOptions = pagesData.map((item) => ({
    value: item.id,
    label: item.name,
  }));

  const filterOption = (
    input: string,
    option?: { label: string; value: number }
  ) => (option?.label ?? "").toLowerCase().includes(input.toLowerCase());

  const fetchRolesData = async () => {
    const content = (
      await request(
        `${location.pathname.split("/").slice(0, 2).join("/")}/getroles`,
        {
          method: "get",
          cancelTokenKey: "getroles",
        }
      )
    )?.content;
    setRolesData(content || []);
  };

  const fetchPagesData = async () => {
    const content = (
      await request(
        `${location.pathname.split("/").slice(0, 2).join("/")}/getpages`,
        {
          method: "get",
          cancelTokenKey: "getpages",
        }
      )
    )?.content;
    setPagesData(content || []);
  };

  return (
    <>
      <Form.Item
        label={intl("role")}
        name="role"
        rules={[
          {
            required: true,
            message: intl("role-required"),
          },
        ]}
      >
        <Select
          onChange={(value) => {
            form.setFieldsValue({ role: value });
          }}
          onDropdownVisibleChange={fetchRolesData}
          options={rolesOptions}
          allowClear
        />
      </Form.Item>
      <Form.Item
        label={intl("pages")}
        name="pages"
        rules={[
          {
            required: true,
            message: intl("pages-required"),
          },
        ]}
      >
        <Select
          mode={edit ? undefined : "multiple"}
          onChange={(_, value) => {
            form.setFieldsValue({
              pages: Array.isArray(value)
                ? value.map((v: any) => v.value)
                : [value],
            });
          }}
          onDropdownVisibleChange={fetchPagesData}
          filterOption={filterOption}
          options={pagesOptions}
          allowClear
        />
      </Form.Item>
      <Form.Item name="access" valuePropName="checked">
        <Checkbox>{intl("allow")}</Checkbox>
      </Form.Item>
    </>
  );
}

export function ButtonAccessSettings({ pathname }: { pathname: string }) {
  const location = useLocation();
  const { intl } = useContext(TranslationContext);
  const { form } = useContext(FormContext);
  const { activeModal } = useContext(ModalContext);
  const edit = activeModal === "edit";
  const [rolesData, setRolesData] = useState<MergedColumnType[]>([]);
  const [pagesData, setPagesData] = useState<MergedColumnType[]>([]);
  const [buttonsData, setButtonsData] = useState<MergedColumnType[]>([]);

  const rolesOptions = rolesData.map((item) => ({
    value: item.id,
    label: item.name,
  }));

  const pagesOptions = pagesData.map((item) => ({
    value: item.id,
    label: item.name,
  }));

  const buttonsOptions = buttonsData.map((item) => ({
    value: item.id,
    label: item.name,
  }));

  const fetchRolesData = async () => {
    const content = (
      await request(
        `${location.pathname.split("/").slice(0, 2).join("/")}/getroles`,
        {
          method: "get",
          cancelTokenKey: "getroles",
        }
      )
    )?.content;
    setRolesData(content || []);
  };

  const fetchPagesData = async () => {
    const content = (
      await request(
        `${location.pathname.split("/").slice(0, 2).join("/")}/getpages`,
        {
          method: "get",
          cancelTokenKey: "getpages",
        }
      )
    )?.content;
    setPagesData(content || []);
  };

  const fetchButtonsData = async () => {
    const content = (
      await request(
        `${location.pathname.split("/").slice(0, 2).join("/")}/getbuttons`,
        {
          method: "get",
          cancelTokenKey: "getbuttons",
        }
      )
    )?.content;
    setButtonsData(content || []);
  };

  return (
    <>
      <Form.Item
        label={intl("role")}
        name="role"
        rules={[
          {
            required: true,
            message: intl("role-required"),
          },
        ]}
      >
        <Select
          onChange={(value) => {
            form.setFieldsValue({ role: value });
          }}
          onDropdownVisibleChange={fetchRolesData}
          options={rolesOptions}
          allowClear
        />
      </Form.Item>
      <Form.Item
        label={intl("pages")}
        name="pages"
        rules={[
          {
            required: true,
            message: intl("pages-required"),
          },
        ]}
      >
        <Select
          mode={edit ? undefined : "multiple"}
          onChange={(_, value) => {
            form.setFieldsValue({
              pages: Array.isArray(value)
                ? value.map((v: any) => v.value)
                : [value],
            });
          }}
          onDropdownVisibleChange={fetchPagesData}
          options={pagesOptions}
          allowClear
        />
      </Form.Item>
      <Form.Item
        label={intl("buttons")}
        name="buttons"
        rules={[
          {
            required: true,
            message: intl("buttons-required"),
          },
        ]}
      >
        <Select
          mode={edit ? undefined : "multiple"}
          onChange={(_, value) => {
            form.setFieldsValue({
              buttons: Array.isArray(value)
                ? value.map((v: any) => v.value)
                : [value],
            });
          }}
          onDropdownVisibleChange={fetchButtonsData}
          options={buttonsOptions}
          allowClear
        />
      </Form.Item>
      <Form.Item name="access" valuePropName="checked">
        <Checkbox>{intl("allow")}</Checkbox>
      </Form.Item>
    </>
  );
}

export function ServerSettings({ pathname }: { pathname: string }) {
  const location = useLocation();
  const { intl } = useContext(TranslationContext);
  const { form } = useContext(FormContext);
  const [connectionSecurity, setConnectionSecurity] = useState<
    MergedColumnType[]
  >([]);

  const connectionSecurityOptions = connectionSecurity.map((item) => ({
    value: item.id,
    label: item.name,
  }));

  const fetchConnectionSecurity = async () => {
    const content = (
      await request(`${location.pathname}/getconnectionsecurity`, {
        method: "get",
        cancelTokenKey: "getconnectionsecurity",
      })
    )?.content;
    setConnectionSecurity(content || []);
  };

  return (
    <>
      <Form.Item
        label={intl("server-name")}
        name="serverName"
        rules={[
          {
            required: true,
            message: intl("server-name-required"),
          },
        ]}
      >
        <Input />
      </Form.Item>
      <Form.Item
        label={intl("server-code")}
        name="serverCode"
        rules={[
          {
            required: true,
            message: intl("server-code-required"),
          },
        ]}
      >
        <Input />
      </Form.Item>
      <Title level={5}>{intl("incoming-mail-server-imap")}</Title>
      <Form.Item label={intl("address")} name="imapAddress">
        <Input />
      </Form.Item>
      <Space className={styles["server-settings-space"]}>
        <Form.Item label={intl("port")} name="imapPort">
          <Input />
        </Form.Item>
        <Form.Item
          label={intl("connection-security")}
          name="imapConnectionSecurity"
        >
          <Select
            labelInValue
            onChange={(value) => {
              form.setFieldsValue({ imapConnectionSecurity: value });
            }}
            onDropdownVisibleChange={fetchConnectionSecurity}
            options={connectionSecurityOptions}
            allowClear
          />
        </Form.Item>
      </Space>
      <Title level={5}>{intl("outgoing-mail-server-smtp")}</Title>
      <Form.Item label={intl("address")} name="smtpAddress">
        <Input />
      </Form.Item>
      <Space className={styles["server-settings-space"]}>
        <Form.Item label={intl("port")} name="smtpPort">
          <Input />
        </Form.Item>
        <Form.Item
          label={intl("connection-security")}
          name="smtpConnectionSecurity"
        >
          <Select
            labelInValue
            onChange={(value) => {
              form.setFieldsValue({ smtpConnectionSecurity: value });
            }}
            onDropdownVisibleChange={fetchConnectionSecurity}
            options={connectionSecurityOptions}
            allowClear
          />
        </Form.Item>
      </Space>
      <Form.Item name="authorization" valuePropName="checked">
        <Checkbox>{intl("authorization")}</Checkbox>
      </Form.Item>
    </>
  );
}

export function Users({ pathname }: { pathname: string }) {
  console.log("Users Employees file Administration");
  const location = useLocation();
  const { intl } = useContext(TranslationContext);
  const { activeModal } = useContext(ModalContext);
  const { form } = useContext(FormContext);
  const { rowData } = useContext(TableContext);
  const { sessionData } = useContext(SessionContext);
  const [rolesData, setRolesData] = useState<MergedColumnType[]>([]);
  const edit = activeModal === "edit";
  const [employeesId, setEmployeesId] = useState<number | undefined>(edit ? sessionData.clerksId : undefined);

  function setFormFields() {
    if (/* edit &&  */ rowData) {
      const fieldsValue: any = {};
      if (rowData["/directories/employees"]) {
        fieldsValue.fullName = rowData["/directories/employees"].id;
        fieldsValue.structuralUnit = `${
          rowData["/directories/employees"].data.name_otdel ?? ""
        }\\${rowData["/directories/employees"].data.posada ?? ""}`;
        setEmployeesId(rowData["/directories/employees"].id);
      }
      form.setFieldsValue(fieldsValue);
    }
  }

  const onGeneratePassword = () => {
    const newPassword = generatePassword();
    form.setFieldsValue({ password: newPassword });
  };

  const rolesOptions = rolesData.map((item) => ({
    value: item.id,
    label: item.name,
  }));

  const fetchRolesData = async () => {
    const content = (
      await request(
        `${location.pathname.split("/").slice(0, 2).join("/")}/getroles`,
        {
          method: "get",
          cancelTokenKey: "getroles",
        }
      )
    )?.content;
    setRolesData(content || []);
  };

  return (
    <>
      <DrawerProvider>
      <Title level={4}>{intl("personal-info")}</Title>
      <Form.Item label={intl("full-name-pib")} name="fullName">
        <Drawer
          url="/directories/employees"
          name={edit ? sessionData.FIO : undefined}
          name2={rowData["/directories/employees"]?.data.fio}
          setFormFields={setFormFields}
          fieldName={"fullName"}
        >
          <Directories url="/directories/employees" selectedRowId={employeesId}/>
        </Drawer>
      </Form.Item>
      <Form.Item label={intl("structural-unit-position")} name="structuralUnit">
        <Input disabled={true} />
      </Form.Item>
      <Form.Item
        label={intl("sed-user")}
        name="login"
        rules={[
          {
            required: true,
            message: intl("sed-user-required"),
          },
        ]}
      >
        <Input />
      </Form.Item>
      <Form.Item
        label={intl("login-email")}
        name="email"
        rules={[
          {
            required: true,
            message: intl("login-email-required"),
          },
          {
            pattern:
              /^(([^<>()[\]\\.,;:\s@\"]+(\.[^<>()[\]\\.,;:\s@\"]+)*)|(\".+\"))@((\[[0-9]{1,3}\.[0-9]{1,3}\.[0-9]{1,3}\.[0-9]{1,3}\])|(([a-zA-Z\-0-9]+\.)+[a-zA-Z]{2,}))$/,
            message: intl("email-pattern"),
          },
        ]}
      >
        <Input />
      </Form.Item>
      <Space className={styles["space"]}>
        <Form.Item
          label={intl("password")}
          name="password"
          rules={[
            {
              // required: !props.data && true,
              message: intl("password-required"),
            },
            {
              pattern:
                /(?=.*[0-9])(?=.*[a-z])(?=.*[A-Z])[0-9a-zA-Z!@#$%^&*_]{8,}/g,
              message: intl("input-correct-password"),
            },
          ]}
        >
          <Input.Password />
        </Form.Item>
        <Form.Item label=" ">
          <Tooltip title={intl("generate-password")}>
            <Button onClick={onGeneratePassword}>
              <KeyOutlined />
            </Button>
          </Tooltip>
        </Form.Item>
      </Space>
      <Form.Item
        label={intl("role")}
        name="role"
        rules={[
          {
            required: true,
            message: intl("role-required"),
          },
        ]}
      >
        <Select
          labelInValue
          onChange={(value) => {
            form.setFieldsValue({ role: value });
          }}
          onDropdownVisibleChange={fetchRolesData}
          options={rolesOptions}
          allowClear
        />
      </Form.Item>
      <Title level={4}>{intl("signs")}</Title>
      <Space size={"large"}>
        <Form.Item name="webVersion" valuePropName="checked">
          <Checkbox disabled={true}>{intl("web-version")}</Checkbox>
        </Form.Item>
        <Form.Item name="allowAccess" valuePropName="checked">
          <Checkbox>{intl("allow-access")}</Checkbox>
        </Form.Item>
        <Form.Item
          name="allowAccessHidden"
          valuePropName="checked"
          hidden={true}
        >
          <Checkbox />
        </Form.Item>
      </Space>
      </DrawerProvider>
      </>
  );
}

export function BarcodeApplicationOptions({ pathname }: { pathname: string }) {
  const location = useLocation();
  const { intl } = useContext(TranslationContext);
  const { activeModal } = useContext(ModalContext);
  const { form } = useContext(FormContext);
  const { rowData } = useContext(TableContext);
  const { sessionData } = useContext(SessionContext);
  const [documentType, setDocumentType] = useState<MergedColumnType[]>([]);
  const [arrangement, setArrangement] = useState<MergedColumnType[]>([]);
  const edit = activeModal === "edit";
  const [departmentId, setDepartmentId] = useState<number | undefined>(edit ? sessionData.sta_otdel_id : undefined);

  const documentTypeOptions = documentType.map((item) => ({
    value: item.id,
    label: item.name,
  }));
  const arrangementOptions = arrangement.map((item) => ({
    value: item.id,
    label: item.name,
  }));
  const imageFormatOptions = [
    { value: "png", label: "png" },
    { value: "bmp", label: "bmp" },
    { value: "jpeg", label: "jpeg" },
    { value: "jpg", label: "jpg" },
  ];

  function setFormFields() {
    if (/* edit &&  */ rowData) {
      const fieldsValue: any = {};
      if (rowData["/directories/structural-units"]) {
        fieldsValue.department = rowData["/directories/structural-units"].id;
        setDepartmentId(rowData["/directories/structural-units"].id);
      }
      form.setFieldsValue(fieldsValue);
    }
  }

  const fetchDocumentType = async () => {
    const content = (
      await request(`${location.pathname}/getdocumenttype`, {
        method: "get",
        cancelTokenKey: "getdocumenttype",
      })
    )?.content;
    setDocumentType(content || []);
  };

  const fetchArrangement = async () => {
    const content = (
      await request(`${location.pathname}/getarrangement`, {
        method: "get",
        cancelTokenKey: "getarrangement",
      })
    )?.content;
    setArrangement(content || []);
  };

  return (
    <>
      <DrawerProvider>
      <Form.Item label={intl("department")} name="department">
        <Drawer
          url="/directories/structural-units"
          name={edit ? sessionData.name_otdel : undefined}
          name2={rowData["/directories/structural-units"]?.data.Name}
          setFormFields={setFormFields}
          fieldName={"department"}
        >
          <Directories url="/directories/structural-units" selectedRowId={departmentId}/>
        </Drawer>
      </Form.Item>
      <Form.Item
        label={intl("document-type")}
        name="documentType"
        rules={[
          {
            required: true,
            message: intl("document-type-required"),
          },
        ]}
      >
        <Select
          labelInValue
          onChange={(value) => {
            form.setFieldsValue({ documentType: value });
          }}
          onDropdownVisibleChange={fetchDocumentType}
          options={documentTypeOptions}
          allowClear
        />
      </Form.Item>
      <Form.Item
        label={intl("arrangement")}
        name="arrangement"
        rules={[
          {
            required: true,
            message: intl("location-required"),
          },
        ]}
      >
        <Select
          labelInValue
          onChange={(value) => {
            form.setFieldsValue({ arrangement: value });
          }}
          onDropdownVisibleChange={fetchArrangement}
          options={arrangementOptions}
          allowClear
        />
      </Form.Item>
      <Title level={5}>{intl("barcode")}</Title>
      <Space>
        {/* Елементи приховано бо не відіграють ролі в налаштуваннях, але не коментовано бо форма обов'язково має мати ці поля */}
        <Form.Item label={intl("barcode-height-cm")} name="barcodeHeight" hidden>
          <InputNumber step={0.1} />
        </Form.Item>
        <Form.Item label={intl("image-format")} name="imageFormat" hidden>
          <Select
            labelInValue
            onChange={(value) => {
              form.setFieldsValue({ imageFormat: value });
            }}
            options={imageFormatOptions}
            allowClear
          />
        </Form.Item>
        <Form.Item name="withDigitalValue" valuePropName="checked" style={{marginLeft: "-15px"}}>
          <Checkbox>{intl("with-a-digital-value")}</Checkbox>
        </Form.Item>
      </Space>
      <Tooltip title={intl("recommended-parameters")}>
        <Title level={5} style={{width: "fit-content", textDecoration: "underline", cursor: "pointer"}}>{intl("inscription")}</Title>
      </Tooltip>
      <Space>
        <Form.Item label={intl("height-cm")} name="height">
          <InputNumber step={0.1} max={1.5} min={0.7} />
        </Form.Item>
        <Form.Item label={intl("width-cm")} name="width">
          <InputNumber step={0.1} max={4.0} min={2.0} />
        </Form.Item>
        <Form.Item label={intl("font-pt")} name="font">
          <InputNumber step={1.0} max={12.0} min={6.0}/>
        </Form.Item>
      </Space>
      <Title level={5}>{intl("electronic-signature")}</Title>
      <Form.Item
        name="addBarcodeWithElectronicSignature"
        valuePropName="checked"
      >
        <Checkbox>
          {intl("add-barcode-when-signing-with-an-electronic-signature")}
        </Checkbox>
      </Form.Item>
      <Form.Item
        label={intl("column-width-of-the-signature-information-table-cm")}
        name="columnWidthSignatureInformation"
      >
        <InputNumber step={0.1} />
      </Form.Item>
      </DrawerProvider>
      </>
  );
}

export function Mailboxes({ pathname }: { pathname: string }) {
  const location = useLocation();
  const { intl } = useContext(TranslationContext);
  const { activeModal } = useContext(ModalContext);
  const { form } = useContext(FormContext);
  const { rowData } = useContext(TableContext);
  const { sessionData } = useContext(SessionContext);
  const edit = activeModal === "edit";
  const [serverId, setServerId] = useState<number | undefined>(edit ? sessionData.mail_Server_id : undefined);

  function setFormFields() {
    if (/* edit &&  */ rowData) {
      const fieldsValue: any = {};
      if (rowData["/administration/server-settings"]) {
        fieldsValue.server = rowData["/administration/server-settings"].id;
        setServerId(rowData["/administration/server-settings"].id);
      }
      form.setFieldsValue(fieldsValue);
    }
  }

  const onCheckConnection = async (btn: string) => {
    const content = await request(`${location.pathname}/checkmailconnection`, {
      method: "post",
      body: {
        btn,
        server: form.getFieldValue("server"),
        login: form.getFieldValue(
          btn === "imap" ? "incomingMailUser" : "outgoingMailUser"
        ),
        password: form.getFieldValue(
          btn === "imap" ? "passwordImap" : "passwordSmtp"
        ),
      },
      cancelTokenKey: "checkmailconnection",
    });

    message.info(
      intl(content.success ? "connection-successful" : "connection-error")
    );
  };

  return (
    <>
      <DrawerProvider>
      <Form.Item label={intl("server")} name="server">
        <Drawer
          url="/administration/server-settings"
          name={edit ? sessionData.ServerName : undefined}
          name2={rowData["/administration/server-settings"]?.data.ServerName}
          setFormFields={setFormFields}
          fieldName={"server"}
        >
          <Directories url="/administration/server-settings" selectedRowId={serverId}/>
        </Drawer>
      </Form.Item>
      <Form.Item label={intl("mailboxe")} name="mailboxe">
        <Input />
      </Form.Item>
      <Form.Item label={intl("user-name")} name="userName">
        <Input />
      </Form.Item>
      <Form.Item label={intl("organization")} name="organization">
        <Input />
      </Form.Item>
      <Title level={5}>{intl("incoming-messages-imap")}</Title>
      <Form.Item label={intl("incoming-mail-user")} name="incomingMailUser">
        <Input />
      </Form.Item>
      <Space align="end">
        <Form.Item label={intl("password")} name="passwordImap">
          <Input.Password />
        </Form.Item>
        <Button onClick={() => onCheckConnection("imap")}>
          {intl("check")}
        </Button>
      </Space>
      <Title level={5}>{intl("outgoing-messages-smtp")}</Title>
      <Form.Item label={intl("outgoing-mail-user")} name="outgoingMailUser">
        <Input />
      </Form.Item>
      <Space align="end">
        <Form.Item label={intl("password")} name="passwordSmtp">
          <Input.Password />
        </Form.Item>
        <Button onClick={() => onCheckConnection("smtp")}>
          {intl("check")}
        </Button>
      </Space>
      </DrawerProvider>
      </>
  );
}

export function MailboxesByUser({ pathname }: { pathname: string }) {
  const { intl } = useContext(TranslationContext);
  const { activeModal } = useContext(ModalContext);
  const { form } = useContext(FormContext);
  const { rowData } = useContext(TableContext);
  const { sessionData } = useContext(SessionContext);
  const edit = activeModal === "edit";
  const [mailboxeId, setMailboxeId] = useState<number | undefined>(edit ? sessionData.email_id : undefined);
  const [mailUser, setMailUserId] = useState<number | undefined>(edit ? sessionData.name_user_mail_id : undefined);

  function setFormFields() {
    if (/* edit &&  */ rowData) {
      const fieldsValue: any = {};
      if (rowData["/administration/mailboxes"]) {
        fieldsValue.mailboxe = rowData["/administration/mailboxes"].id;
        setMailboxeId(rowData["/administration/mailboxes"].id);
      }
      if (rowData["/administration/users"]) {
        fieldsValue.mailUser = rowData["/administration/users"].id;
        setMailUserId(rowData["/administration/users"].id);
      }
      form.setFieldsValue(fieldsValue);
    }
  }

  return (
    <>
      <DrawerProvider>
      <Form.Item
        label={intl("mailboxe")}
        name="mailboxe"
        rules={[
          {
            required: true,
            message: intl("mailbox-required"),
          },
        ]}
      >
        <Drawer
          url="/administration/mailboxes"
          name={edit ? sessionData.email : undefined}
          name2={rowData["/administration/mailboxes"]?.data.email}
          setFormFields={setFormFields}
          fieldName={"mailboxe"}
        >
          <Directories url="/administration/mailboxes" selectedRowId={mailboxeId} />
        </Drawer>
      </Form.Item>
      <Form.Item
        label={intl("mail-user")}
        name="mailUser"
        rules={[
          {
            required: true,
            message: intl("mail-user-required"),
          },
        ]}
      >
        <Drawer
          url="/administration/users"
          name={edit ? sessionData.name_user_mail : undefined}
          name2={rowData["/administration/users"]?.data.FIO}
          setFormFields={setFormFields}
          fieldName={"mailUser"}
        >
          <Directories url="/administration/users" selectedRowId={mailUser} />
        </Drawer>
      </Form.Item>
      </DrawerProvider>
      </>
  );
}
